<template>
  <div class="edit-quote-modal-form-row">
    <InputWrapper
      v-slot="{ ariaProps, inputClasses, inputId, invalid }"
      input-id="lane-quote-effective-date"
      :errors="v$.effectiveDate.$errors"
      :label="t('rateShop.details.effectiveDate')"
    >
      <RygenCalendar
        :id="inputId"
        v-model="modelValue.effectiveDate"
        v-bind="ariaProps"
        :class="inputClasses"
        :invalid="invalid"
        :show-time="false"
        show-icon
        :placeholder="t('rateShop.details.effectiveDate')"
      />
    </InputWrapper>

    <InputWrapper
      v-slot="{ ariaProps, inputClasses, inputId, invalid }"
      input-id="lane-quote-expiration-date"
      :errors="v$.expirationDate.$errors"
      :label="t('rateShop.details.quoteExpiration')"
    >
      <RygenCalendar
        :id="inputId"
        v-model="modelValue.expirationDate"
        v-bind="ariaProps"
        :class="inputClasses"
        :invalid="invalid"
        :show-time="false"
        show-icon
        :placeholder="t('rateShop.details.quoteExpiration')"
      />
    </InputWrapper>

    <InputWrapper
      v-slot="{ ariaProps, inputClasses, inputId, invalid }"
      input-id="lane-quote-origin-options"
      :errors="v$.origin.$errors"
      :label="t('orders.origin')"
      class="w-4"
    >
      <RygenMultiSelect
        v-bind="ariaProps"
        :id="inputId"
        v-model="originLocationOptions"
        :class="inputClasses"
        :invalid="invalid"
        :options="locationMultiSelectOptions"
        option-label="label"
        :placeholder="t('orders.origin')"
      />
    </InputWrapper>

    <InputWrapper
      v-slot="{ ariaProps, inputClasses, inputId, invalid }"
      input-id="lane-quote-dest-options"
      :errors="v$.destination.$errors"
      :label="t('orders.destination')"
      class="w-4"
    >
      <RygenMultiSelect
        v-bind="ariaProps"
        :id="inputId"
        v-model="destinationLocationOptions"
        :class="inputClasses"
        :invalid="invalid"
        :options="locationMultiSelectOptions"
        option-label="label"
        :placeholder="t('orders.destination')"
      />
    </InputWrapper>
  </div>
</template>

<script lang="ts" setup>
import { PropType } from 'vue';
import { useI18n } from 'vue-i18n';

import {
  afterDateTime,
  beforeDateTime,
  required,
  serverValidationPlaceholder,
  useValidations,
} from '@rygen/rygen-ui/composables';

import {
  EmbeddableLocation,
  LaneQuoteData,
  LaneQuoteLocationOption,
} from '@/model';

import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';
import RygenCalendar from '@rygen/rygen-ui/components/form/RygenCalendar.vue';
import RygenMultiSelect from '@rygen/rygen-ui/components/form/RygenMultiSelect.vue';

const modelValue = defineModel({
  type: LaneQuoteData,
  default: () => new LaneQuoteData(),
});

const originLocationOptions = defineModel('originLocationOptions', {
  type: [] as PropType<LaneQuoteLocationOption[]>,
  default: () => [],
});

const destinationLocationOptions = defineModel('destinationLocationOptions', {
  type: [] as PropType<LaneQuoteLocationOption[]>,
  default: () => [],
});

const { t } = useI18n();

const rules = {
  effectiveDate: {
    required: required(t('rateShop.details.effectiveDate')),
    before: beforeDateTime(
      () => modelValue.value.expirationDate,
      t('rateShop.details.effectiveDate'),
      t('rateShop.details.quoteExpiration'),
      { strict: false },
    ),
  },
  expirationDate: {
    required: required(t('rateShop.details.quoteExpiration')),
    after: afterDateTime(
      () => modelValue.value.effectiveDate,
      t('rateShop.details.quoteExpiration'),
      t('rateShop.details.effectiveDate'),
      { strict: false },
    ),
  },
  origin: { serverValidationPlaceholder },
  destination: { serverValidationPlaceholder },
};

const { externalResults, v$ } = useValidations(modelValue, rules);

const locationMultiSelectOptions: LaneQuoteLocationOption[] = [
  {
    label: t('address.address'),
    apply(
      laneLocation: EmbeddableLocation,
      location: EmbeddableLocation,
    ): EmbeddableLocation {
      if (laneLocation === undefined) {
        laneLocation = new EmbeddableLocation();
      }
      laneLocation.addressLine1 = location.addressLine1;
      laneLocation.addressLine2 = location.addressLine2;
      //addressLine3 is intentionally ommitted because Lightning doesn't use it
      laneLocation.city = location.city;
      laneLocation.state = location.state;
      laneLocation.postalCode = location.postalCode;
      laneLocation.country = location.country;

      return laneLocation;
    },
  },
  {
    label: t('rateShop.cityState'),
    apply(
      laneLocation: EmbeddableLocation,
      location: EmbeddableLocation,
    ): EmbeddableLocation {
      if (laneLocation === undefined) {
        laneLocation = new EmbeddableLocation();
      }
      laneLocation.city = location.city;
      laneLocation.state = location.state;
      laneLocation.country = location.country;

      return laneLocation;
    },
  },
  {
    label: t('address.postalCode'),
    apply(
      laneLocation: EmbeddableLocation,
      location: EmbeddableLocation,
    ): EmbeddableLocation {
      if (laneLocation === undefined) {
        laneLocation = new EmbeddableLocation();
      }
      laneLocation.postalCode = location.postalCode;
      laneLocation.country = location.country;

      return laneLocation;
    },
  },
  {
    label: t('address.country'),
    apply(
      laneLocation: EmbeddableLocation,
      location: EmbeddableLocation,
    ): EmbeddableLocation {
      if (laneLocation === undefined) {
        laneLocation = new EmbeddableLocation();
      }
      laneLocation.country = location.country;

      return laneLocation;
    },
  },
];

const clearExternalValidations = () => {
  v$.value.$clearExternalResults();
};

const validateOriginDestination = (): boolean => {
  let isValid = true;

  if (originLocationOptions.value.length < 1) {
    isValid = false;
    externalResults.value = {
      ...externalResults.value,
      origin: [
        t('validations.required', {
          label: t('orders.origin'),
        }),
      ],
    };
  }

  if (destinationLocationOptions.value.length < 1) {
    isValid = false;
    externalResults.value = {
      ...externalResults.value,
      destination: [
        t('validations.required', {
          label: t('orders.destination'),
        }),
      ],
    };
  }
  return isValid;
};

defineExpose({ clearExternalValidations, validateOriginDestination });
</script>
